import { useState, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { filter, find, forEach, includes, some } from 'lodash'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { CameraIcon } from '@heroicons/react/solid'
import { Dialog } from '@headlessui/react'
import Switch from 'components/atoms/Switch'
import Selection from 'components/molecules/Selection'
import { ImageCropper } from 'components/atoms/ImageCropper'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiGet from 'lib/network/apiGet'
import notify from 'components/atoms/Notify'

const CAR_IMAGE_TILE_CLASS = 'relative aspect-video w-full border border-dashed border-gray-300 rounded-md overflow-hidden bg-gray-100'
const CAR_IMAGE_IMG_CLASS = 'h-full w-full object-cover rounded'
const VEHICLE_IMAGE_DND = 'vehicle-model-image'

const DraggableImageTile = ({ image, index, moveImage, onRemove }) => {
  const ref = useRef(null)

  const [{ isDragging }, drag] = useDrag({
    type: VEHICLE_IMAGE_DND,
    item: { index },
    collect: monitor => ({ isDragging: monitor.isDragging() }),
  })

  const [, drop] = useDrop({
    accept: VEHICLE_IMAGE_DND,
    hover: item => {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return
      moveImage(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  drag(drop(ref))

  return (
    <div ref={ref} className={`${CAR_IMAGE_TILE_CLASS} group cursor-move ${isDragging ? 'opacity-40 ring-2 ring-dark-500' : ''}`}>
      <img src={image.kind === 'pending' ? image.previewUrl : image.url || image.thumbnailUrl} alt="Vehicle" className={CAR_IMAGE_IMG_CLASS} draggable={false} />
      <span className="absolute top-1 left-1 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-dark-600 text-[10px] font-bold text-white shadow">{index + 1}</span>
      <button type="button" onClick={onRemove} className="absolute block sm:hidden sm:group-hover:block top-1 right-1 bg-white rounded-full p-1 shadow-md hover:bg-gray-200">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}

const AddVehicleModelProfileModal = ({ onSuccess, close, id = null, close: customClose }) => {
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'
  const fileInputRef = useRef(null)
  const croppedFilesRef = useRef([])
  const suppressCropCloseRef = useRef(false)

  const [formData, setFormData] = useState({
    make: '',
    model: '',
    modelYear: '',
    active: true,
    hasRealImages: false,
  })
  const [makes, setMakes] = useState([])
  const [models, setModels] = useState([])
  const [selectedMakes, setSelectedMakes] = useState([])
  const [selectedModels, setSelectedModels] = useState([])
  const [images, setImages] = useState([])
  const imagesRef = useRef([])
  const [loading, setLoading] = useState(false)

  const [isCropModalOpen, setIsCropModalOpen] = useState(false)
  const [cropQueue, setCropQueue] = useState([])
  const [cropQueueIndex, setCropQueueIndex] = useState(0)
  const [cropSrc, setCropSrc] = useState(null)

  const getLocalizedLabel = (enValue, arValue) => {
    if (isArabic) {
      return arValue || enValue || ''
    }
    return enValue || arValue || ''
  }

  const createOption = (optionId, enValue, arValue) => ({
    id: optionId,
    enName: enValue || '',
    arName: arValue || '',
    name: getLocalizedLabel(enValue || '', arValue || ''),
  })

  const modelYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear()
    const years = []
    for (let year = currentYear; year >= 1995; year -= 1) {
      years.push({ id: String(year), name: String(year) })
    }
    return years
  }, [])

  useEffect(() => {
    fetchMakes()
    fetchModels()
    if (id) {
      fetchProfileData()
    }
  }, [id])

  useEffect(() => {
    imagesRef.current = images
  }, [images])

  useEffect(() => {
    return () => {
      forEach(filter(imagesRef.current, { kind: 'pending' }), img => URL.revokeObjectURL(img.previewUrl))
    }
  }, [])

  useEffect(() => {
    if (!isCropModalOpen) return undefined

    const releaseSuppress = () => {
      // Keep suppress briefly so the click that fires after a drag outside does not close the modal
      window.setTimeout(() => {
        suppressCropCloseRef.current = false
      }, 150)
    }

    window.addEventListener('pointerup', releaseSuppress)
    window.addEventListener('pointercancel', releaseSuppress)
    return () => {
      window.removeEventListener('pointerup', releaseSuppress)
      window.removeEventListener('pointercancel', releaseSuppress)
    }
  }, [isCropModalOpen])

  const fetchMakes = async (searchQuery = '') => {
    try {
      const response = await apiGet('makes', { search: searchQuery, pageSize: 500, pageNumber: 1 })
      if (response?.data) {
        const makesList = response.data.makes.map(make => createOption(make._id, make.make, make.makeAr))
        const combinedMakes = [...makesList]
        forEach(selectedMakes, selectedMake => {
          if (!find(combinedMakes, { id: selectedMake.id })) {
            combinedMakes.push(selectedMake)
          }
        })
        setMakes(combinedMakes)
      }
    } catch (error) {
      notify.error(t('addVehicleModelProfileModal.messages.failedToFetchMakes'))
    }
  }

  const fetchModels = async (searchQuery = '') => {
    try {
      const response = await apiGet('modals', { search: searchQuery, pageSize: 500, pageNumber: 1 })
      if (response?.data) {
        const modelsList = response.data.modals.map(model => createOption(model._id, model.modal, model.modalAr))
        const combinedModels = [...modelsList]
        forEach(selectedModels, selectedModel => {
          if (!find(combinedModels, { id: selectedModel.id })) {
            combinedModels.push(selectedModel)
          }
        })
        setModels(combinedModels)
      }
    } catch (error) {
      notify.error(t('addVehicleModelProfileModal.messages.failedToFetchModels'))
    }
  }

  const fetchProfileData = async () => {
    try {
      const response = await apiGet(`vehicle-model-profile/${id}`)
      if (response.success) {
        const data = response.data
        const makeId = data.make?._id || data.make || ''
        const modelId = data.model?._id || data.model || ''

        if (data.make) {
          const makeOption = createOption(makeId, data.make.make, data.make.makeAr)
          setSelectedMakes([makeOption])
          setMakes(prev => (find(prev, { id: makeOption.id }) ? prev : [makeOption, ...prev]))
        }

        if (data.model) {
          const modelOption = createOption(modelId, data.model.modal, data.model.modalAr)
          setSelectedModels([modelOption])
          setModels(prev => (find(prev, { id: modelOption.id }) ? prev : [modelOption, ...prev]))
        }

        setFormData({
          make: makeId,
          model: modelId,
          modelYear: data.modelYear ? String(data.modelYear) : '',
          active: data.active !== undefined ? data.active : true,
          hasRealImages: Boolean(data.hasRealImages),
        })

        const serverImages =
          data.carImages?.length > 0
            ? data.carImages.map(img => ({
                _id: img._id,
                url: img.url,
                thumbnailUrl: img.thumbnailUrl || img.url,
              }))
            : data.url || data.thumbnailUrl
            ? [{ _id: 'primary', url: data.url || data.thumbnailUrl, thumbnailUrl: data.thumbnailUrl || data.url }]
            : []

        setImages(
          serverImages.map(img => ({
            key: `existing-${img._id}`,
            kind: 'existing',
            imageId: img._id,
            url: img.url,
            thumbnailUrl: img.thumbnailUrl,
          }))
        )
      } else {
        notify.error(t('addVehicleModelProfileModal.messages.failedToFetchProfileData'))
      }
    } catch (error) {
      console.error('Error fetching vehicle model profile:', error)
      notify.error(t('addVehicleModelProfileModal.messages.failedToFetchProfileData'))
    }
  }

  const handleMakeChange = selectedValue => {
    const makeId = selectedValue?.id || ''
    if (selectedValue) {
      setSelectedMakes([selectedValue])
    }
    setFormData(prev => ({ ...prev, make: makeId }))
  }

  const handleModelChange = selectedValue => {
    const modelId = selectedValue?.id || ''
    if (selectedValue) {
      setSelectedModels([selectedValue])
    }
    setFormData(prev => ({ ...prev, model: modelId }))
  }

  const handleModelYearChange = selectedValue => {
    setFormData(prev => ({ ...prev, modelYear: selectedValue?.id || '' }))
  }

  const handleActiveChange = checked => {
    setFormData(prev => ({ ...prev, active: checked }))
  }

  const handleHasRealImagesChange = checked => {
    setFormData(prev => ({ ...prev, hasRealImages: checked }))
  }

  const revokeCropSrc = () => {
    if (cropSrc) {
      URL.revokeObjectURL(cropSrc)
      setCropSrc(null)
    }
  }

  const resetCropState = () => {
    revokeCropSrc()
    setIsCropModalOpen(false)
    setCropQueue([])
    setCropQueueIndex(0)
    croppedFilesRef.current = []
  }

  const openCropForFile = (file, index = 0) => {
    revokeCropSrc()
    setCropSrc(URL.createObjectURL(file))
    setCropQueueIndex(index)
    setIsCropModalOpen(true)
  }

  const handleImagesSelected = event => {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    if (!files.length) return

    const invalid = some(files, file => !includes(['image/jpeg', 'image/jpg', 'image/png'], file.type))
    if (invalid) {
      notify.error(t('addVehicleModelProfileModal.messages.invalidImageType'))
      return
    }

    croppedFilesRef.current = []
    setCropQueue(files)
    openCropForFile(files[0], 0)
  }

  const closeCropModal = () => {
    resetCropState()
  }

  const handleCropDialogClose = () => {
    if (suppressCropCloseRef.current) return
    closeCropModal()
  }

  const handleCropPointerDown = () => {
    suppressCropCloseRef.current = true
  }

  const handleCropConfirm = croppedFile => {
    const nextCropped = [...croppedFilesRef.current, croppedFile]
    croppedFilesRef.current = nextCropped
    const nextIndex = cropQueueIndex + 1

    if (nextIndex < cropQueue.length) {
      openCropForFile(cropQueue[nextIndex], nextIndex)
      return
    }

    const pending = nextCropped.map((file, index) => ({
      key: `pending-${Date.now()}-${index}-${file.name}`,
      kind: 'pending',
      file,
      previewUrl: URL.createObjectURL(file),
    }))
    setImages(prev => [...prev, ...pending])
    resetCropState()
  }

  const moveImage = (dragIndex, hoverIndex) => {
    setImages(prev => {
      const updated = [...prev]
      const [moved] = updated.splice(dragIndex, 1)
      updated.splice(hoverIndex, 0, moved)
      return updated
    })
  }

  const removeImage = imageKey => {
    setImages(prev => {
      const target = find(prev, { key: imageKey })
      if (target?.kind === 'pending') {
        URL.revokeObjectURL(target.previewUrl)
      }
      return prev.filter(img => img.key !== imageKey)
    })
  }

  const handleSubmit = async () => {
    if (!formData.make) {
      notify.error(t('addVehicleModelProfileModal.messages.makeRequired'))
      return
    }
    if (!formData.model) {
      notify.error(t('addVehicleModelProfileModal.messages.modelRequired'))
      return
    }
    if (!formData.modelYear) {
      notify.error(t('addVehicleModelProfileModal.messages.modelYearRequired'))
      return
    }

    if (images.length === 0) {
      notify.error(t('addVehicleModelProfileModal.messages.imageRequired'))
      return
    }

    const existingImages = filter(images, { kind: 'existing' })
    const pendingImages = filter(images, { kind: 'pending' })

    const payload = new FormData()
    payload.append('make', formData.make)
    payload.append('model', formData.model)
    payload.append('modelYear', formData.modelYear)
    payload.append('active', String(formData.active))
    payload.append('hasRealImages', String(formData.hasRealImages))

    if (id) {
      const hasLegacyPrimary = some(existingImages, { imageId: 'primary' })
      if (!hasLegacyPrimary) {
        payload.append('keptImageIds', JSON.stringify(existingImages.map(img => img.imageId).filter(Boolean)))
        const pendingOrder = new Map(pendingImages.map((img, index) => [img.key, index]))
        payload.append('imageOrder', JSON.stringify(images.map(img => (img.kind === 'existing' ? img.imageId : `new:${pendingOrder.get(img.key)}`))))
      }
    }

    forEach(pendingImages, img => {
      payload.append('images', img.file)
    })

    try {
      setLoading(true)
      let response
      if (id) {
        response = await apiPut(`vehicle-model-profile/${id}`, payload)
        if (response.success) {
          notify.success(t('addVehicleModelProfileModal.messages.profileUpdatedSuccessfully'))
        }
      } else {
        response = await apiPost('vehicle-model-profile', payload)
        if (response.success) {
          notify.success(t('addVehicleModelProfileModal.messages.profileAddedSuccessfully'))
        }
      }

      if (response.success) {
        onSuccess()
        if (customClose) customClose()
        close()
      } else {
        notify.error(response.message || t('addVehicleModelProfileModal.messages.operationFailed'))
      }
    } catch (error) {
      console.error('Error saving vehicle model profile:', error)
      notify.error(t('addVehicleModelProfileModal.messages.failedToSaveProfile'))
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    if (customClose) customClose()
    close()
  }

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('addVehicleModelProfileModal.labels.make')}</label>
        <Selection
          onChange={handleMakeChange}
          title={t('addVehicleModelProfileModal.placeholders.selectMake')}
          multiple={false}
          list={makes}
          value={formData.make}
          showSearch
          onSearch={searchQuery => fetchMakes(searchQuery)}
          className="w-full"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('addVehicleModelProfileModal.labels.model')}</label>
        <Selection
          onChange={handleModelChange}
          title={t('addVehicleModelProfileModal.placeholders.selectModel')}
          multiple={false}
          list={models}
          value={formData.model}
          showSearch
          onSearch={searchQuery => fetchModels(searchQuery)}
          className="w-full"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('addVehicleModelProfileModal.labels.modelYear')}</label>
        <Selection
          onChange={handleModelYearChange}
          title={t('addVehicleModelProfileModal.placeholders.selectModelYear')}
          multiple={false}
          list={modelYearOptions}
          value={formData.modelYear}
          className="w-full"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">{t('addVehicleModelProfileModal.labels.vehicleImages')}</label>
          {images.length > 1 && <span className="text-xs text-gray-500">{t('addVehicleModelProfileModal.labels.dragToReorder')}</span>}
        </div>
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png" multiple onChange={handleImagesSelected} className="hidden" />
        <DndProvider backend={HTML5Backend}>
          <div className="grid grid-cols-3 gap-3">
            {images.map((image, index) => (
              <DraggableImageTile key={image.key} image={image} index={index} moveImage={moveImage} onRemove={() => removeImage(image.key)} />
            ))}

            <div onClick={() => fileInputRef.current?.click()} className={`${CAR_IMAGE_TILE_CLASS} cursor-pointer flex items-center justify-center`}>
              <CameraIcon className="text-gray-400 h-8 w-8" />
            </div>
          </div>
        </DndProvider>
      </div>

      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">{t('addVehicleModelProfileModal.labels.hasRealImages')}</label>
        <Switch value={formData.hasRealImages} onChange={handleHasRealImagesChange} />
      </div>

      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">{t('addVehicleModelProfileModal.labels.active')}</label>
        <Switch value={formData.active} onChange={handleActiveChange} />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          onClick={handleCancel}
          disabled={loading}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {t('addVehicleModelProfileModal.buttons.cancel')}
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 disabled:opacity-50"
        >
          {id ? t('addVehicleModelProfileModal.buttons.updateProfile') : t('addVehicleModelProfileModal.buttons.addProfile')}
        </button>
      </div>

      <Dialog open={isCropModalOpen} onClose={handleCropDialogClose} className="relative z-[1300]">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" onClick={handleCropDialogClose} />

        <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto pointer-events-none">
          <Dialog.Panel className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl pointer-events-auto" onClick={event => event.stopPropagation()}>
            <Dialog.Title className="text-lg font-medium mb-1">
              Crop image
              {cropQueue.length > 1 ? ` (${cropQueueIndex + 1} of ${cropQueue.length})` : ''}
            </Dialog.Title>
            <p className="text-sm text-gray-500 mb-4">Adjust the crop, zoom, and rotation, then preview before uploading.</p>

            {cropSrc && (
              <div onPointerDown={handleCropPointerDown}>
                <ImageCropper
                  key={cropSrc}
                  currentFile={cropSrc}
                  fileName={cropQueue[cropQueueIndex]?.name || 'car-image.jpg'}
                  aspectRatio={16 / 9}
                  onConfirm={handleCropConfirm}
                  close={closeCropModal}
                />
              </div>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  )
}

export default AddVehicleModelProfileModal
